import type { ComponentType, ReactNode, RefObject } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@voll-intelligence/ui-kit/ui/tooltip';
import {
  FIELD_TRIGGER_EMPTY_CLASS,
  FIELD_TRIGGER_GLYPH_CLASS,
  FieldTrigger,
  wantedAttr,
} from './field_trigger.js';

/* O gatilho-pílula dos campos de busca com um ícone e um resumo (hotel:
   Quem/Quando; rent a car: Retirada/Devolução). Nasceu na barra do hotel, foi
   extraído quando a Página do Hotel passou a montar os MESMOS campos sobre os
   quartos, e saiu do namespace do hotel quando o rent a car (#2441) precisou da
   mesma pílula. A pele é a `FieldTrigger` compartilhada — aqui só mora o par
   ícone + texto com o tooltip do valor cortado. */

/** O valor AINDA NÃO respondido. Na página o desenho o quer um degrau mais
 *  claro que o rótulo (`--sand-500` contra o `--muted-foreground` dele) e sem o
 *  peso médio do valor preenchido — é o que separa pergunta de resposta numa
 *  fileira sem casca. No card os dois vivem no mesmo cinza. */
function mutedValueClass(naked: boolean): string {
  return naked ? FIELD_TRIGGER_EMPTY_CLASS : 'text-muted-foreground';
}

/**
 * Um campo da barra/fileira: ícone + resumo que ENCURTA com reticências, num
 * gatilho-pílula que abre o controle. O tooltip devolve o texto cortado, e
 * `accessibleText` existe porque a data mostra "20–22 set · 2 noites" mas
 * anuncia o intervalo por extenso.
 *
 * Sem teste do tooltip de propósito: o tooltip do Base UI não abre sob jsdom
 * (`userEvent.hover` não satisfaz o modelo de ponteiro dele, mesmo esperando o
 * delay), então o que existiria aqui seria um teste vermelho. Verificado no
 * navegador. O valor cheio segue coberto pelo `aria-label` do gatilho, esse sim
 * testado.
 */
export function SearchField({
  triggerRef,
  icon: Icon,
  glyph,
  label,
  text,
  accessibleText,
  muted = false,
  naked = false,
  wanted = false,
  expanded,
  disabled,
  onOpen,
}: {
  readonly triggerRef: RefObject<HTMLButtonElement | null>;
  readonly icon: ComponentType<{ className?: string }>;
  /** Glifo que SUBSTITUI o ícone quando o campo tem uma identidade a mostrar em
   *  vez de um conceito — o retrato do hóspede no QUEM. Ausente ⇒ o `icon`. */
  readonly glyph?: ReactNode;
  readonly label: string;
  readonly text: string;
  readonly accessibleText?: string;
  readonly muted?: boolean;
  readonly naked?: boolean;
  /** O campo PEDE A VEZ: o anel de pendente (`data-wanted`, ver
   *  `FIELD_TRIGGER_OPEN_RING_CLASS`) — o próximo passo do preenchimento
   *  guiado. Ausente ⇒ sem anel. */
  readonly wanted?: boolean;
  readonly expanded: boolean;
  readonly disabled: boolean;
  readonly onOpen: () => void;
}): ReactNode {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <FieldTrigger
            /* `outline` na página, `ghost` no card: sem a pílula-card em volta,
               é a borda de 1px que desenha o campo. Na página o campo OCUPA a
               coluna dele (`fill`); no card a fileira é `w-fit`. */
            variant={naked ? 'outline' : 'ghost'}
            fill={naked}
            ref={triggerRef}
            disabled={disabled}
            aria-haspopup="dialog"
            aria-expanded={expanded}
            {...wantedAttr(wanted)}
            aria-label={`${label} · ${accessibleText ?? text}`}
            onClick={onOpen}
          />
        }
      >
        {glyph ?? <Icon className={FIELD_TRIGGER_GLYPH_CLASS} />}
        <span className={`text-ui min-w-0 truncate ${muted ? mutedValueClass(naked) : ''}`}>
          {text}
        </span>
      </TooltipTrigger>
      <TooltipContent>{text}</TooltipContent>
    </Tooltip>
  );
}
